import React, { useState, useEffect } from 'react';
import {
  BeakerIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  CalculatorIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  CubeTransparentIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Define valid exercise types
export type NaturalSciencesExerciseType = 
  | 'virtual-lab' 
  | 'chemical-equations' 
  | 'physics-simulation' 
  | 'biological-classification' 
  | 'hypothesis-testing';

// Helper function to check if exercise type is valid
export const isValidNaturalSciencesExerciseType = (type: string): type is NaturalSciencesExerciseType => {
  const validTypes: NaturalSciencesExerciseType[] = [
    'virtual-lab',
    'chemical-equations', 
    'physics-simulation',
    'biological-classification',
    'hypothesis-testing'
  ];
  return validTypes.includes(type as NaturalSciencesExerciseType);
};

interface NaturalSciencesInteractiveProps {
  content: {
    id: string;
    title: string;
    description: string;
    type: string;
    interactiveData: {
      exerciseType: NaturalSciencesExerciseType;
      instructions: string;
      scenario?: any;
      expectedResults?: any;
      hints?: string[];
    };
  };
  onComplete: (completed: boolean, score?: number) => void;
}

const NaturalSciencesInteractive: React.FC<NaturalSciencesInteractiveProps> = ({
  content,
  onComplete
}) => {
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [results, setResults] = useState<Record<string, any>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [currentHint, setCurrentHint] = useState(0);
  const [experimentStage, setExperimentStage] = useState(0);
  const [simulationRunning, setSimulationRunning] = useState(false);
  
  // Tracking states
  const [startTime] = useState(new Date());
  const [timeSpent, setTimeSpent] = useState(0);
  const [windowFocused, setWindowFocused] = useState(true);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [inputInteractions, setInputInteractions] = useState(0);
  const [minimumEngagementTime] = useState(180); // 3 minutes for sciences
  const [isEngaged, setIsEngaged] = useState(false);

  const { exerciseType, instructions, scenario, hints } = content.interactiveData;

  // Debug logging and validation for exercise type
  useEffect(() => {
    console.log('NaturalSciencesInteractive Debug:', {
      exerciseType,
      contentType: content.type,
      interactiveData: content.interactiveData,
      isValidType: isValidNaturalSciencesExerciseType(exerciseType || '')
    });

    if (exerciseType && !isValidNaturalSciencesExerciseType(exerciseType)) {
      console.error(`Invalid natural sciences exercise type: "${exerciseType}"`);
      toast.error(`Invalid exercise type: "${exerciseType}". Please check the configuration.`);
    }
  }, [exerciseType, content]);

  useEffect(() => {
    initializeExercise();
  }, [exerciseType]);

  // Window focus tracking
  useEffect(() => {
    if (isSubmitted) return;

    const handleFocus = () => setWindowFocused(true);
    const handleBlur = () => {
      setWindowFocused(false);
      setTabSwitches(prev => prev + 1);
      if (tabSwitches >= 2) {
        toast('⚠️ Stay focused on your experiment for accurate results.', {
          icon: '🧪',
          duration: 3000,
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWindowFocused(false);
      } else {
        setWindowFocused(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSubmitted, tabSwitches]);

  // Time tracking
  useEffect(() => {
    if (isSubmitted) return;

    const interval = setInterval(() => {
      if (windowFocused) {
        setTimeSpent(prev => {
          const newTime = prev + 1;
          if (newTime >= minimumEngagementTime && !isEngaged) {
            setIsEngaged(true);
          }
          return newTime;
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [windowFocused, isSubmitted, minimumEngagementTime, isEngaged]);

  const initializeExercise = () => {
    if (!exerciseType) {
      console.warn('NaturalSciencesInteractive: No exerciseType provided');
      setInputs({});
      return;
    }

    switch (exerciseType) {
      case 'virtual-lab':
        setInputs({
          hypothesis: '',
          materials: ['', '', ''],
          procedure: ['', '', '', ''],
          observations: ['', '', ''],
          conclusion: '',
          variables: { independent: '', dependent: '', control: '' }
        });
        setExperimentStage(0);
        break;
      case 'chemical-equations':
        setInputs({
          reactants: scenario?.unbalancedEquation?.reactants || [],
          products: scenario?.unbalancedEquation?.products || [],
          coefficients: {},
          balancingSteps: ['']
        });
        break;
      case 'physics-simulation':
        setInputs({
          initialValues: {},
          predictions: '',
          measurements: [],
          calculations: '',
          analysis: ''
        });
        break;
      case 'biological-classification':
        setInputs({
          specimens: scenario?.specimens?.map((s: any) => ({ 
            ...s, 
            userClassification: { kingdom: '', phylum: '', class: '', order: '', family: '', genus: '', species: '' }
          })) || [],
          reasoning: ''
        });
        break;
      case 'hypothesis-testing':
        setInputs({
          nullHypothesis: '',
          alternativeHypothesis: '',
          variables: { independent: '', dependent: '' },
          methodology: '',
          dataAnalysis: '',
          conclusion: ''
        });
        break;
      default:
        console.warn(`NaturalSciencesInteractive: Unknown exercise type "${exerciseType}"`);
        setInputs({});
        break;
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setInputInteractions(prev => prev + 1);
  };

  const validateSubmission = () => {
    if (!isEngaged) {
      toast.error(`Please spend at least ${Math.ceil(minimumEngagementTime / 60)} minutes conducting your experiment.`);
      return false;
    }

    const requiredFields = getRequiredFields();
    const missingFields = requiredFields.filter(field => {
      const value = inputs[field];
      return !value || (Array.isArray(value) ? value.every(v => !v) : !value.length);
    });
    
    if (missingFields.length > 0) {
      toast.error('Please complete all required fields before submitting.');
      return false;
    }

    return true;
  };

  const getRequiredFields = () => {
    switch (exerciseType) {
      case 'virtual-lab':
        return ['hypothesis', 'conclusion'];
      case 'chemical-equations':
        return ['coefficients'];
      case 'physics-simulation':
        return ['predictions', 'analysis'];
      case 'biological-classification':
        return ['reasoning'];
      case 'hypothesis-testing':
        return ['nullHypothesis', 'alternativeHypothesis', 'methodology'];
      default:
        return [];
    }
  };

  const evaluateSubmission = () => {
    switch (exerciseType) {
      case 'virtual-lab':
        return evaluateVirtualLab();
      case 'chemical-equations':
        return evaluateChemicalEquations();
      case 'physics-simulation':
        return evaluatePhysicsSimulation();
      case 'biological-classification':
        return evaluateBiologicalClassification();
      case 'hypothesis-testing':
        return evaluateHypothesisTesting();
      default:
        return { score: 0, feedback: 'Unknown exercise type' };
    }
  };

  const evaluateVirtualLab = () => {
    let score = 0;
    const feedback = [];

    // Hypothesis (20 points)
    if (inputs.hypothesis?.length > 30 && (inputs.hypothesis.includes('if') || inputs.hypothesis.includes('predict'))) {
      score += 20;
      feedback.push('✓ Well-formed hypothesis');
    } else {
      feedback.push('Hypothesis should be testable and use "if...then" format');
    }

    // Variables identification (15 points)
    const variables = inputs.variables || {};
    if (variables.independent && variables.dependent) {
      score += 15;
      feedback.push('✓ Variables correctly identified');
    }

    // Materials and procedure (20 points)
    const validMaterials = inputs.materials?.filter((m: string) => m.length > 5).length || 0;
    const validSteps = inputs.procedure?.filter((p: string) => p.length > 10).length || 0;
    if (validMaterials >= 2 && validSteps >= 3) {
      score += 20;
      feedback.push('✓ Complete materials list and procedure');
    }

    // Observations (25 points)
    const validObservations = inputs.observations?.filter((o: string) => o.length > 15).length || 0;
    score += Math.min(25, validObservations * 8.33);
    if (validObservations >= 2) {
      feedback.push('✓ Detailed observations recorded');
    }

    // Conclusion (20 points)
    if (inputs.conclusion?.length > 50) {
      score += 20;
      feedback.push('✓ Comprehensive conclusion');
    } else {
      feedback.push('Conclusion should relate back to your hypothesis');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateChemicalEquations = () => {
    let score = 0;
    const feedback = [];

    // Check if equation is balanced
    const expectedCoefficients = scenario?.correctCoefficients || {};
    const userCoefficients = inputs.coefficients || {};
    
    let correctCoefficients = 0;
    let totalCoefficients = Object.keys(expectedCoefficients).length;
    
    Object.keys(expectedCoefficients).forEach(compound => {
      if (userCoefficients[compound] === expectedCoefficients[compound]) {
        correctCoefficients++;
      }
    });

    // Coefficient accuracy (70 points)
    score += Math.round((correctCoefficients / totalCoefficients) * 70);
    
    if (correctCoefficients === totalCoefficients) {
      feedback.push('✓ Chemical equation correctly balanced');
    } else {
      feedback.push(`${correctCoefficients}/${totalCoefficients} coefficients correct`);
    }

    // Balancing steps explanation (30 points)
    const validSteps = inputs.balancingSteps?.filter((s: string) => s.length > 20).length || 0;
    score += Math.min(30, validSteps * 10);
    if (validSteps >= 2) {
      feedback.push('✓ Balancing process well explained');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluatePhysicsSimulation = () => {
    let score = 0;
    const feedback = [];

    // Predictions (25 points)
    if (inputs.predictions?.length > 40) {
      score += 25;
      feedback.push('✓ Clear predictions made');
    }

    // Measurements (25 points)
    const validMeasurements = inputs.measurements?.filter((m: any) => 
      m.value && m.unit && m.time !== undefined
    ).length || 0;
    score += Math.min(25, validMeasurements * 5);

    // Calculations (25 points)
    if (inputs.calculations?.length > 30) {
      score += 25;
      feedback.push('✓ Calculations shown');
    }

    // Analysis (25 points)
    if (inputs.analysis?.length > 50) {
      score += 25;
      feedback.push('✓ Thorough analysis provided');
    } else {
      feedback.push('Analysis should compare predictions with results');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateBiologicalClassification = () => {
    let score = 0;
    const feedback = [];

    // Classification accuracy
    const specimens = inputs.specimens || [];
    let totalClassifications = 0;
    let correctClassifications = 0;

    specimens.forEach((specimen: any) => {
      const expected = specimen.correctClassification || {};
      const user = specimen.userClassification || {};
      
      ['kingdom', 'phylum', 'class', 'order', 'family', 'genus', 'species'].forEach(level => {
        if (expected[level]) {
          totalClassifications++;
          if (user[level]?.toLowerCase() === expected[level]?.toLowerCase()) {
            correctClassifications++;
          }
        }
      });
    });

    // Classification points (70 points)
    if (totalClassifications > 0) {
      score += Math.round((correctClassifications / totalClassifications) * 70);
      feedback.push(`Classification accuracy: ${correctClassifications}/${totalClassifications}`);
    }

    // Reasoning (30 points)
    if (inputs.reasoning?.length > 60) {
      score += 30;
      feedback.push('✓ Classification reasoning provided');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateHypothesisTesting = () => {
    let score = 0;
    const feedback = [];

    // Null hypothesis (20 points)
    if (inputs.nullHypothesis?.length > 20) {
      score += 20;
      feedback.push('✓ Null hypothesis stated');
    }

    // Alternative hypothesis (20 points)
    if (inputs.alternativeHypothesis?.length > 20) {
      score += 20;
      feedback.push('✓ Alternative hypothesis stated');
    }

    // Methodology (25 points)
    if (inputs.methodology?.length > 50) {
      score += 25;
      feedback.push('✓ Research methodology described');
    }

    // Data analysis (20 points)
    if (inputs.dataAnalysis?.length > 30) {
      score += 20;
      feedback.push('✓ Data analysis approach outlined');
    }

    // Conclusion (15 points)
    if (inputs.conclusion?.length > 30) {
      score += 15;
      feedback.push('✓ Conclusion drawn');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const runSimulation = () => {
    if (simulationRunning) return;

    setSimulationRunning(true);
    toast.success('Running simulation...');

    // Simulate physics calculation
    setTimeout(() => {
      const mockResults = generateSimulationResults();
      handleInputChange('measurements', mockResults);
      setSimulationRunning(false);
      toast.success('Simulation complete! Check your measurements.');
    }, 3000);
  };

  const generateSimulationResults = () => {
    // Mock physics simulation results
    const results = [];
    for (let t = 0; t <= 10; t += 1) {
      results.push({
        time: t,
        value: Math.round((0.5 * 9.81 * t * t + Math.random() * 2 - 1) * 100) / 100,
        unit: 'm'
      });
    }
    return results;
  };

  const handleSubmit = () => {
    if (!validateSubmission()) return;

    const evaluation = evaluateSubmission();
    const finalScore = Math.max(0, Math.min(100, evaluation.score));
    const passed = finalScore >= 70;

    setResults({
      score: finalScore,
      feedback: evaluation.feedback,
      passed,
      timeSpent,
      interactions: inputInteractions
    });

    setIsSubmitted(true);
    setIsCorrect(passed);
    
    onComplete(passed, finalScore);

    if (passed) {
      toast.success(`Excellent scientific work! Score: ${finalScore}%`);
    } else {
      toast.error(`Score: ${finalScore}%. Review the feedback and try again.`);
    }
  };

  const getExerciseIcon = () => {
    switch (exerciseType) {
      case 'virtual-lab': return <BeakerIcon className="h-6 w-6" />;
      case 'chemical-equations': return <FireIcon className="h-6 w-6" />;
      case 'physics-simulation': return <CalculatorIcon className="h-6 w-6" />;
      case 'biological-classification': return <MagnifyingGlassIcon className="h-6 w-6" />;
      case 'hypothesis-testing': return <ChartBarIcon className="h-6 w-6" />;
      default: return <CubeTransparentIcon className="h-6 w-6" />;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderExerciseContent = () => {
    switch (exerciseType) {
      case 'virtual-lab':
        return renderVirtualLab();
      case 'chemical-equations':
        return renderChemicalEquations();
      case 'physics-simulation':
        return renderPhysicsSimulation();
      case 'biological-classification':
        return renderBiologicalClassification();
      case 'hypothesis-testing':
        return renderHypothesisTesting();
      default:
        return (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h4 className="font-semibold text-red-800 mb-2">❌ Unknown Exercise Type</h4>
            <p className="text-red-700 mb-3">
              Exercise type "{exerciseType}" is not recognized. Please check the configuration.
            </p>
            <div className="bg-red-100 p-3 rounded text-sm">
              <strong>Supported exercise types:</strong>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>virtual-lab</li>
                <li>chemical-equations</li>
                <li>physics-simulation</li>
                <li>biological-classification</li>
                <li>hypothesis-testing</li>
              </ul>
              <p className="mt-2">
                <strong>Current value:</strong> "{exerciseType}"
              </p>
            </div>
          </div>
        );
    }
  };

  const renderVirtualLab = () => (
    <div className="space-y-6">
      {/* Lab Setup */}
      {scenario?.labSetup && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Lab Setup:</h4>
          <div className="text-sm text-gray-700">{scenario.labSetup}</div>
        </div>
      )}

      {/* Experiment Stages */}
      <div className="flex space-x-2 mb-4">
        {['Setup', 'Hypothesis', 'Experiment', 'Analysis'].map((stage, index) => (
          <button
            key={index}
            onClick={() => setExperimentStage(index)}
            className={`px-3 py-1 rounded-lg text-sm font-medium ${
              experimentStage === index
                ? 'bg-green-600 text-white'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
          >
            {stage}
          </button>
        ))}
      </div>

      {/* Stage Content */}
      {experimentStage === 0 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Variables
            </label>
            <div className="grid md:grid-cols-3 gap-4">
              <input
                type="text"
                value={inputs.variables?.independent || ''}
                onChange={(e) => handleInputChange('variables', { 
                  ...inputs.variables, 
                  independent: e.target.value 
                })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                placeholder="Independent Variable"
                disabled={isSubmitted}
              />
              <input
                type="text"
                value={inputs.variables?.dependent || ''}
                onChange={(e) => handleInputChange('variables', { 
                  ...inputs.variables, 
                  dependent: e.target.value 
                })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                placeholder="Dependent Variable"
                disabled={isSubmitted}
              />
              <input
                type="text"
                value={inputs.variables?.control || ''}
                onChange={(e) => handleInputChange('variables', { 
                  ...inputs.variables, 
                  control: e.target.value 
                })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                placeholder="Control Variables"
                disabled={isSubmitted}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Materials List
            </label>
            {[0, 1, 2].map((index) => (
              <input
                key={index}
                type="text"
                value={inputs.materials?.[index] || ''}
                onChange={(e) => {
                  const newMaterials = [...(inputs.materials || ['', '', ''])];
                  newMaterials[index] = e.target.value;
                  handleInputChange('materials', newMaterials);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                placeholder={`Material ${index + 1}`}
                disabled={isSubmitted}
              />
            ))}
          </div>
        </div>
      )}

      {experimentStage === 1 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hypothesis *
          </label>
          <textarea
            value={inputs.hypothesis || ''}
            onChange={(e) => handleInputChange('hypothesis', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
            rows={3}
            placeholder="If... then... because... (State your testable prediction)"
            disabled={isSubmitted}
          />
        </div>
      )}

      {experimentStage === 2 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Procedure Steps
            </label>
            {[0, 1, 2, 3].map((index) => (
              <textarea
                key={index}
                value={inputs.procedure?.[index] || ''}
                onChange={(e) => {
                  const newProcedure = [...(inputs.procedure || ['', '', '', ''])];
                  newProcedure[index] = e.target.value;
                  handleInputChange('procedure', newProcedure);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                rows={2}
                placeholder={`Step ${index + 1}: Describe what you will do`}
                disabled={isSubmitted}
              />
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Observations
            </label>
            {[0, 1, 2].map((index) => (
              <textarea
                key={index}
                value={inputs.observations?.[index] || ''}
                onChange={(e) => {
                  const newObservations = [...(inputs.observations || ['', '', ''])];
                  newObservations[index] = e.target.value;
                  handleInputChange('observations', newObservations);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 mb-2"
                rows={2}
                placeholder={`Observation ${index + 1}: Record what you see/measure`}
                disabled={isSubmitted}
              />
            ))}
          </div>
        </div>
      )}

      {experimentStage === 3 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Conclusion *
          </label>
          <textarea
            value={inputs.conclusion || ''}
            onChange={(e) => handleInputChange('conclusion', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
            rows={4}
            placeholder="Was your hypothesis supported? What did you learn? What could be improved?"
            disabled={isSubmitted}
          />
        </div>
      )}
    </div>
  );

  const renderChemicalEquations = () => (
    <div className="space-y-6">
      {scenario?.unbalancedEquation && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Balance this chemical equation:</h4>
          <div className="text-lg font-mono text-center">
            {scenario.unbalancedEquation.reactants.join(' + ')} → {scenario.unbalancedEquation.products.join(' + ')}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Coefficients
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {scenario?.unbalancedEquation && 
            [...scenario.unbalancedEquation.reactants, ...scenario.unbalancedEquation.products].map((compound: string, index: number) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  value={inputs.coefficients?.[compound] || ''}
                  onChange={(e) => handleInputChange('coefficients', {
                    ...inputs.coefficients,
                    [compound]: parseInt(e.target.value) || 1
                  })}
                  className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                  disabled={isSubmitted}
                />
                <span className="text-sm">{compound}</span>
              </div>
            ))
          }
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Balancing Steps (Show your work)
        </label>
        {[0, 1, 2].map((index) => (
          <textarea
            key={index}
            value={inputs.balancingSteps?.[index] || ''}
            onChange={(e) => {
              const newSteps = [...(inputs.balancingSteps || ['', '', ''])];
              newSteps[index] = e.target.value;
              handleInputChange('balancingSteps', newSteps);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 mb-2"
            rows={2}
            placeholder={`Step ${index + 1}: Explain your balancing process`}
            disabled={isSubmitted}
          />
        ))}
      </div>

      {/* Balanced equation preview */}
      {Object.keys(inputs.coefficients || {}).length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Your balanced equation:</h4>
          <div className="text-lg font-mono text-center">
            {scenario?.unbalancedEquation?.reactants.map((r: string, i: number) => 
              `${inputs.coefficients?.[r] || 1}${r}`).join(' + ')} → {' '}
            {scenario?.unbalancedEquation?.products.map((p: string, i: number) => 
              `${inputs.coefficients?.[p] || 1}${p}`).join(' + ')}
          </div>
        </div>
      )}
    </div>
  );

  const renderPhysicsSimulation = () => (
    <div className="space-y-6">
      {scenario?.simulationSetup && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Physics Simulation Setup:</h4>
          <div className="text-sm text-gray-700">{scenario.simulationSetup}</div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Initial Predictions *
        </label>
        <textarea
          value={inputs.predictions || ''}
          onChange={(e) => handleInputChange('predictions', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="What do you predict will happen and why?"
          disabled={isSubmitted}
        />
      </div>

      {/* Simulation Controls */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium">Physics Simulation</h4>
            <p className="text-sm text-blue-100">Run the simulation to collect data</p>
          </div>
          <button
            onClick={runSimulation}
            disabled={simulationRunning || isSubmitted}
            className={`px-4 py-2 rounded-lg font-medium flex items-center ${
              simulationRunning 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-white text-blue-600 hover:bg-gray-100'
            }`}
          >
            {simulationRunning ? (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              'Run Simulation'
            )}
          </button>
        </div>
      </div>

      {/* Measurements Table */}
      {inputs.measurements && inputs.measurements.length > 0 && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Simulation Results:</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-3 py-2 text-left">Time (s)</th>
                  <th className="px-3 py-2 text-left">Value</th>
                  <th className="px-3 py-2 text-left">Unit</th>
                </tr>
              </thead>
              <tbody>
                {inputs.measurements.slice(0, 6).map((measurement: any, index: number) => (
                  <tr key={index} className="border-t">
                    <td className="px-3 py-2">{measurement.time}</td>
                    <td className="px-3 py-2">{measurement.value}</td>
                    <td className="px-3 py-2">{measurement.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Calculations
        </label>
        <textarea
          value={inputs.calculations || ''}
          onChange={(e) => handleInputChange('calculations', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="Show your calculations using the simulation data"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Analysis *
        </label>
        <textarea
          value={inputs.analysis || ''}
          onChange={(e) => handleInputChange('analysis', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={4}
          placeholder="Compare your predictions with the simulation results. What did you learn?"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  const renderBiologicalClassification = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-2">Taxonomic Classification</h4>
        <p className="text-sm text-gray-600">
          Classify each specimen using the standard taxonomic hierarchy: Kingdom → Phylum → Class → Order → Family → Genus → Species
        </p>
      </div>

      {inputs.specimens?.map((specimen: any, specimenIndex: number) => (
        <div key={specimenIndex} className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center mb-4">
            {specimen.imageUrl && (
              <img 
                src={specimen.imageUrl} 
                alt={`Specimen ${specimenIndex + 1}`}
                className="w-24 h-24 object-cover rounded-lg mr-4"
              />
            )}
            <div>
              <h5 className="font-medium">Specimen {specimenIndex + 1}</h5>
              <p className="text-sm text-gray-600">{specimen.description}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {['kingdom', 'phylum', 'class', 'order', 'family', 'genus', 'species'].map((level) => (
              <input
                key={level}
                type="text"
                value={specimen.userClassification?.[level] || ''}
                onChange={(e) => {
                  const newSpecimens = [...inputs.specimens];
                  newSpecimens[specimenIndex].userClassification = {
                    ...newSpecimens[specimenIndex].userClassification,
                    [level]: e.target.value
                  };
                  handleInputChange('specimens', newSpecimens);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                placeholder={level.charAt(0).toUpperCase() + level.slice(1)}
                disabled={isSubmitted}
              />
            ))}
          </div>
        </div>
      ))}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Classification Reasoning *
        </label>
        <textarea
          value={inputs.reasoning || ''}
          onChange={(e) => handleInputChange('reasoning', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
          rows={4}
          placeholder="Explain the key characteristics you used to classify each specimen"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  const renderHypothesisTesting = () => (
    <div className="space-y-6">
      {scenario?.researchContext && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Research Context:</h4>
          <div className="text-sm text-gray-700">{scenario.researchContext}</div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Null Hypothesis (H₀) *
        </label>
        <textarea
          value={inputs.nullHypothesis || ''}
          onChange={(e) => handleInputChange('nullHypothesis', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={2}
          placeholder="State the null hypothesis (no effect/difference)"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Alternative Hypothesis (H₁) *
        </label>
        <textarea
          value={inputs.alternativeHypothesis || ''}
          onChange={(e) => handleInputChange('alternativeHypothesis', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={2}
          placeholder="State the alternative hypothesis (effect/difference exists)"
          disabled={isSubmitted}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Independent Variable
          </label>
          <input
            type="text"
            value={inputs.variables?.independent || ''}
            onChange={(e) => handleInputChange('variables', { 
              ...inputs.variables, 
              independent: e.target.value 
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
            placeholder="What you manipulate"
            disabled={isSubmitted}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Dependent Variable
          </label>
          <input
            type="text"
            value={inputs.variables?.dependent || ''}
            onChange={(e) => handleInputChange('variables', { 
              ...inputs.variables, 
              dependent: e.target.value 
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
            placeholder="What you measure"
            disabled={isSubmitted}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Research Methodology *
        </label>
        <textarea
          value={inputs.methodology || ''}
          onChange={(e) => handleInputChange('methodology', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={4}
          placeholder="Describe your experimental design, sample size, controls, and procedures"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Data Analysis Plan
        </label>
        <textarea
          value={inputs.dataAnalysis || ''}
          onChange={(e) => handleInputChange('dataAnalysis', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={3}
          placeholder="What statistical tests will you use? How will you interpret the results?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Expected Conclusion
        </label>
        <textarea
          value={inputs.conclusion || ''}
          onChange={(e) => handleInputChange('conclusion', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
          rows={2}
          placeholder="What do you expect to conclude from your research?"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getExerciseIcon()}
            <div className="ml-3">
              <h3 className="text-xl font-bold">{content.title}</h3>
              <p className="text-green-100 text-sm">{content.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Focus indicator */}
            <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
              windowFocused ? 'bg-green-500 bg-opacity-20 text-green-100' : 'bg-red-500 bg-opacity-20 text-red-100'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-1 ${
                windowFocused ? 'bg-green-300' : 'bg-red-300'
              }`}></div>
              {windowFocused ? 'Focused' : 'Not Focused'}
            </div>
            
            <div className="text-sm">
              Time: {formatTime(timeSpent)}
            </div>
            
            {isEngaged && (
              <div className="flex items-center text-xs px-2 py-1 rounded-full bg-green-500 bg-opacity-20 text-green-100">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Engaged
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <p className="text-gray-700">{instructions}</p>
        
        {/* Engagement progress */}
        {!isEngaged && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>Minimum experiment time</span>
              <span>{formatTime(timeSpent)} / {formatTime(minimumEngagementTime)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((timeSpent / minimumEngagementTime) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Exercise Content */}
      <div className="p-6">
        {renderExerciseContent()}

        {/* Hints Section */}
        {hints && hints.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setShowHints(!showHints)}
              className="flex items-center text-green-600 hover:text-green-700 mb-3"
            >
              <LightBulbIcon className="h-4 w-4 mr-2" />
              {showHints ? 'Hide Hints' : 'Show Hints'}
            </button>
            
            {showHints && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-800">
                    Hint {currentHint + 1} of {hints.length}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentHint(Math.max(0, currentHint - 1))}
                      disabled={currentHint === 0}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentHint(Math.min(hints.length - 1, currentHint + 1))}
                      disabled={currentHint === hints.length - 1}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
                <p className="text-yellow-700 text-sm">{hints[currentHint]}</p>
              </div>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-6">
          {!isSubmitted ? (
            <button
              onClick={handleSubmit}
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              Submit Experiment
            </button>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                {isCorrect ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-600 mr-2" />
                )}
                <span className="font-medium">
                  Score: {results.score}% {isCorrect ? '(Passed)' : '(Review Required)'}
                </span>
              </div>
              <div className="text-sm text-gray-600 whitespace-pre-line">
                {results.feedback}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Time spent: {formatTime(results.timeSpent)} | Interactions: {results.interactions}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NaturalSciencesInteractive;